/**
 * One table for everything a crawler or a share sheet reads about a public
 * route: title, description, canonical URL, Open Graph and Twitter tags, and
 * schema.org JSON-LD. Two consumers:
 *
 *   - vite.config.ts writes a static HTML shell per route at build time
 *     (build/<route>/index.html) because Facebook, iMessage, Slack and X do
 *     not run scripts; the CloudFront router serves those shells.
 *   - usePageMeta keeps the same tags current during client-side navigation.
 *
 * The facts here (dates, hotel, price, fund link) are the ones confirmed on
 * 2026-09-08 and mirrored in the API seed; the live pages read them from
 * settings, this is the build-time copy. Keep this module free of browser
 * imports: it also runs in Node inside the Vite config.
 */

export const SITE_URL = 'https://brandonstrong.com';
export const SITE_NAME = 'BrandonStrong.com';

export const DEFAULT_DESCRIPTION =
  'A celebration of life for Brandon Taylor at SpringHill Suites Lakeland, Saturday October 3, 2026. Reserve a room and support Shawna. #BrandonStrong';

export const EVENT = {
  name: 'Celebration of Life for Brandon Taylor',
  /** Saturday welcome night, doors at 6 PM, to the Sunday check-out. Hotel time (Eastern). */
  start: '2026-10-03T18:00:00-04:00',
  end: '2026-10-04T12:00:00-04:00',
  hotel: {
    name: 'SpringHill Suites by Marriott Lakeland',
    street: '511 West Lime Street',
    city: 'Lakeland',
    region: 'FL',
    postalCode: '33815',
    phone: '+1 863-413-1700',
  },
  /** Whole price per room for the night: no tax or fees on top. */
  roomPrice: 299,

  donationUrl: 'https://gofund.me/633a26073',
} as const;

export const OG_IMAGE = {
  url: `${SITE_URL}/og-image.jpg`,
  type: 'image/jpeg',
  width: 1200,
  height: 630,
  alt: 'Brandon Taylor. Celebration of Life, Saturday October 3, 2026, Lakeland. #BrandonStrong',
} as const;

export type JsonLd = Record<string, unknown>;

export interface SeoPage {
  readonly path: string;
  /** Page part of the title; empty for the home page (site name only). */
  readonly title: string;
  readonly description: string;
  readonly jsonLd: readonly JsonLd[];
}

const hotelPlace: JsonLd = {
  '@type': 'Hotel',
  name: EVENT.hotel.name,
  telephone: EVENT.hotel.phone,
  address: {
    '@type': 'PostalAddress',
    streetAddress: EVENT.hotel.street,
    addressLocality: EVENT.hotel.city,
    addressRegion: EVENT.hotel.region,
    postalCode: EVENT.hotel.postalCode,
    addressCountry: 'US',
  },
};

const eventLd: JsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Event',
  name: EVENT.name,
  description:
    'A weekend celebrating Brandon Taylor: Saturday welcome night with dinner and music, Sunday farewell. Rockstar dress code. Rooms held at the hotel for Saturday night.',
  startDate: EVENT.start,
  endDate: EVENT.end,
  eventStatus: 'https://schema.org/EventScheduled',
  eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
  location: hotelPlace,
  image: [OG_IMAGE.url],
  url: `${SITE_URL}/event`,
  organizer: { '@type': 'Organization', name: "Brandon's family", url: SITE_URL },
  offers: [
    {
      '@type': 'Offer',
      name: 'Room for Saturday night, king or two queens',
      price: String(EVENT.roomPrice),
      priceCurrency: 'USD',
      url: `${SITE_URL}/rooms`,
      availability: 'https://schema.org/InStock',
      validFrom: '2026-09-08',
    },
    {
      '@type': 'Offer',
      name: 'RSVP for the celebration (suggested donation)',
      price: '0',
      priceCurrency: 'USD',
      url: `${SITE_URL}/rsvp`,
      availability: 'https://schema.org/InStock',
      validFrom: '2026-09-11',
    },
  ],
  keywords: 'BrandonStrong',
};

const websiteLd: JsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  description: DEFAULT_DESCRIPTION,
  inLanguage: 'en-US',
};

const donateLd: JsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Donate',
  url: `${SITE_URL}/donate`,
  description: 'Support Shawna through the GoFundMe set up for Brandon.',
  potentialAction: {
    '@type': 'DonateAction',
    name: 'Donate to the family',
    target: EVENT.donationUrl,
  },
};

const photosLd: JsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ImageGallery',
  name: 'Photos of Brandon',
  url: `${SITE_URL}/photos`,
  description: "Photos of Brandon Taylor from his family and friends.",
};

const guestbookLd: JsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Guestbook',
  url: `${SITE_URL}/guestbook`,
  description: 'Messages for Brandon and the family from the people who love him.',
};

const contactLd: JsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact & FAQ',
  url: `${SITE_URL}/contact`,
  about: hotelPlace,
};

export const SEO = {
  home: { path: '/', title: '', description: DEFAULT_DESCRIPTION, jsonLd: [websiteLd, eventLd] },
  event: {
    path: '/event',
    title: 'The Event',
    description: 'When, where, what to wear, and what to expect at the celebration of life.',
    jsonLd: [eventLd],
  },
  photos: {
    path: '/photos',
    title: 'Photos',
    description: 'Photos of Brandon from his family and friends, across the years.',
    jsonLd: [photosLd],
  },
  guestbook: {
    path: '/guestbook',
    title: 'Guestbook',
    description:
      'Leave a message for Brandon and the family, and read what the people who love him have written.',
    jsonLd: [guestbookLd],
  },
  rooms: {
    path: '/rooms',
    title: 'Rooms',
    description:
      'Reserve a room at the hotel for the celebration. Live availability, paid in full at booking.',
    jsonLd: [eventLd],
  },
  rsvp: {
    path: '/rsvp',
    title: 'RSVP',
    description:
      'Coming to the celebration of life but not staying at the hotel? RSVP with the names of your party and, if you like, a donation to Shawna.',
    jsonLd: [eventLd],
  },
  donate: {
    path: '/donate',
    title: 'Donate',
    description: 'Support Shawna through the GoFundMe set up for Brandon.',
    jsonLd: [donateLd],
  },
  contact: {
    path: '/contact',
    title: 'Contact & FAQ',
    description: 'How to reach the family and the hotel, and answers to common questions.',
    jsonLd: [contactLd],
  },
  terms: {
    path: '/terms',
    title: 'Terms & no-refund policy',
    description:
      'Reservation terms for the celebration: every room is paid in full at booking and is non-refundable.',
    jsonLd: [],
  },
  privacy: {
    path: '/privacy',
    title: 'Privacy',
    description: 'What this site keeps about a reservation, and what it never stores.',
    jsonLd: [],
  },
} as const satisfies Record<string, SeoPage>;

/** Every public route that gets its own prerendered shell. */
export const PAGES: readonly SeoPage[] = Object.values(SEO);

export function fullTitle(title: string): string {
  return title ? `${title} · ${SITE_NAME}` : SITE_NAME;
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function meta(key: 'name' | 'property', name: string, content: string): string {
  return `<meta ${key}="${name}" content="${escapeAttr(content)}" />`;
}

/** JSON inside a <script> must never be able to close the tag. */
function jsonLdScript(block: JsonLd): string {
  const json = JSON.stringify(block).replace(/</g, '\\u003c');
  return `<script type="application/ld+json">${json}</script>`;
}

/** The head tags for one route, as the static shell carries them. */
export function renderHead(page: SeoPage): string {
  const url = `${SITE_URL}${page.path}`;
  const title = fullTitle(page.title);
  return [
    `<title>${escapeAttr(title)}</title>`,
    meta('name', 'description', page.description),
    `<link rel="canonical" href="${url}" />`,
    meta('name', 'robots', 'index, follow'),
    meta('property', 'og:type', 'website'),
    meta('property', 'og:site_name', SITE_NAME),
    meta('property', 'og:locale', 'en_US'),
    meta('property', 'og:title', title),
    meta('property', 'og:description', page.description),
    meta('property', 'og:url', url),
    meta('property', 'og:image', OG_IMAGE.url),
    meta('property', 'og:image:type', OG_IMAGE.type),
    meta('property', 'og:image:width', String(OG_IMAGE.width)),
    meta('property', 'og:image:height', String(OG_IMAGE.height)),
    meta('property', 'og:image:alt', OG_IMAGE.alt),
    meta('name', 'twitter:card', 'summary_large_image'),
    meta('name', 'twitter:title', title),
    meta('name', 'twitter:description', page.description),
    meta('name', 'twitter:image', OG_IMAGE.url),
    meta('name', 'twitter:image:alt', OG_IMAGE.alt),
    ...page.jsonLd.map(jsonLdScript),
  ].join('\n    ');
}
